import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { FIRST_PAGE, MAX_PER_PAGE } from 'constants/constants';
import { QueryTranslationRequestsArgs, TranslationRequestPage } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import { buildQueryKey } from 'utils/buildQueryKey';
import clientUtil from 'utils/clientUtil';

type TVariables = QueryTranslationRequestsArgs;
type TData = TranslationRequestPage;

const MOCK: TVariables = {
  comments: undefined,
  createdAfter: undefined,
  createdBefore: undefined,
  editorId: undefined,
  page: FIRST_PAGE,
  perPage: MAX_PER_PAGE,
  proposedClientDeadlineAfter: undefined,
  proposedClientDeadlineBefore: undefined,
  quality: undefined,
  query: undefined,
  showDuplicates: undefined,
  skipPagination: undefined,
  sortAttribute: undefined,
  sortOrder: undefined,
  sourceLanguageCode: undefined,
  status: undefined,
  targetLanguageCode: undefined,
  version: undefined,
  translatedByAgent: undefined,
  workflowId: undefined
};

export const useTranslationRequests = (
  variables: TVariables = MOCK,
  options: Omit<UseQueryOptions<unknown, TGenericError, TData>, 'queryKey'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query translationRequests(
      $query: String
      $comments: CommentsFilterEnum
      $status: [TranslationRequestStatusFilterEnum]
      $quality: QualityEnum
      $showDuplicates: Boolean
      $targetLanguageCode: String
      $sourceLanguageCode: String
      $editorId: ID
      $createdAfter: ISO8601DateTime
      $createdBefore: ISO8601DateTime
      $proposedClientDeadlineBefore: ISO8601DateTime
      $proposedClientDeadlineAfter: ISO8601DateTime
      $page: Int
      $perPage: Int
      $translatedByAgent: Boolean
      $workflowId: ID
    ) {
      translationRequests(
        query: $query
        comments: $comments
        status: $status
        quality: $quality
        showDuplicates: $showDuplicates
        targetLanguageCode: $targetLanguageCode
        sourceLanguageCode: $sourceLanguageCode
        editorId: $editorId
        createdAfter: $createdAfter
        createdBefore: $createdBefore
        proposedClientDeadlineBefore: $proposedClientDeadlineBefore
        proposedClientDeadlineAfter: $proposedClientDeadlineAfter
        page: $page
        perPage: $perPage
        translatedByAgent: $translatedByAgent
        workflowId: $workflowId
      ) {
        nodes {
          id
          integration
          status
          styleGuideReferenceId
          purchaseOrderCode
          purchaseOrder {
            orderCode
          }
          proposedClientDeadline
          version # #number displayed on ss
          title # TR title
          duplicateOf
          pully
          uploaded
          sourceLanguageCode # From language
          targetLanguageCode #To language
          quality #quality
          createdAt # date created
          totalWordCount #total words
          totalOrderedWordCount # orderable words
          waitingInReviewSince
          aiTranslationWorkflow {
            id
          }
          pendingTasks {
            id
            status
          }
          comments {
            id
            read
            writtenByClient
            task {
              id
            }
          }
          order {
            paidAt
            acceptedAt
          }
          tasks {
            id
          } # tasks
          orderDetails {
            duplicateOfVersion
            pricingData {
              # pricing data for quoted
              price
              pricePerWord
              savings
            }
            sentences {
              orderableWordsCopy
              savings
              totalOrderableWords
              totalWords {
                perLanguage
                targetLanguagesCount
                total
              }
            }
          }
          #isQuote = status = quoted
          #isShowTranslation = status finished
        }
        hasNextPage
        hasPreviousPage
        nodesCount
        pagesCount
      }
    }
  `;

  return useQuery<unknown, TGenericError, TData>({
    ...options,
    queryKey: ['useTranslationRequests', ...buildQueryKey({ ...MOCK, ...variables })],
    queryFn: async () =>
      client.request({
        data: {
          gql: graphql,
          variables
        }
      })
  });
};
